<template>
  <portal :to="getPortalTitle">
    <img
      alt=""
      class="authority-service-logo"
      src="/svg/authority-services/ras-logo.svg"
    />
  </portal>

  <portal :to="getPortalSubtitle">
    <div class="font-weight-semibold text-body-large mb-4">
      {{ i18n.t('titles.mobile_id_verification') }}
    </div>
    <div class="text-black mb-6">
      {{ i18n.t('texts.check_identity_ras_hint') }}
    </div>
  </portal>

  <div class="d-flex flex-column" style="width: 100%">
    <v-row no-gutters>
      <v-col :cols="12" :md="6">
        <v-text-field
          v-model="form.firstname"
          :class="{
            'mr-2': display.mdAndUp.value,
            'mb-5': !display.mdAndUp.value
          }"
          disabled
          hide-details="auto"
          :label="i18n.t('labels.firstname')"
          variant="outlined"
        />
      </v-col>
      <v-col :cols="12" :md="6">
        <v-text-field
          v-model="form.lastname"
          :class="{ 'ml-2': display.mdAndUp.value }"
          disabled
          hide-details="auto"
          :label="i18n.t('labels.lastname')"
          variant="outlined"
        />
      </v-col>
    </v-row>
    <v-row class="mt-5" no-gutters>
      <v-col cols="12">
        <v-text-field
          v-model="form.displayName"
          data-test-id="mobile-id-display-name"
          :error-messages="displayNameErrors"
          hide-details="auto"
          :label="i18n.t('labels.display_name')"
          variant="outlined"
          @update:model-value="clearErrors"
        />
      </v-col>
    </v-row>
    <v-row class="mt-5 mb-2" no-gutters>
      <v-col cols="12">
        <DeepFieldPhone
          v-bind="bindProps"
          id="phone-input"
          v-model="form.phoneNumber"
          :country-label="i18n.t('labels.country')"
          data-test-id="mobile-id-phone-number"
          density="compact"
          :error-messages="phoneErrorMessages"
          :hide-details="false"
          :label="i18n.t('labels.phone_number') + '*'"
          :placeholder="String(i18n.t('placeholders.phone_number'))"
          :valid-characters-only="true"
          :validate="validatePhone"
          variant="outlined"
          @update:phone-object="onPhoneUpdate"
        />
      </v-col>
    </v-row>
  </div>
  <portal :to="getPortalActions">
    <VBtnSecondary
      :text="i18n.t('buttons.cancel')"
      variant="outlined"
      @click="handleCancel"
    />
    <VBtnPrimary
      color="primary"
      data-test-id="mobile-id-save-button"
      :disabled="!isFormValid || updatePending"
      :loading="updatePending"
      :text="i18n.t('buttons.save')"
      @click="updateDeepAdminUser"
    />
  </portal>
</template>

<script setup lang="ts">
import { DeepFieldPhone } from '@deepcloud/deep-ui-lib';
import AuthService from '@/services/authService.ts';
import { useDeepAdminStore } from '@/stores/deepadmin/deepadmin';
import { useSignStore } from '@/stores/sign.ts';
import { computed, inject, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEmitter } from '@/composables/useEmitter';
import type { errorType, formType } from '@/types/PhoneForm';
import type { ParsedPhoneNumber } from 'awesome-phonenumber';
import { constants } from '@/constants/constants';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify/framework';
import { useApi } from '@/composables/useApi.ts';
import type { AvailableAuthorityServices } from '@/types/SignDocument.ts';
import { useDeepAdminInternalStore } from '@/stores/deepadmin/deepadmin-internal.ts';
import { batchProcessMachineKey } from '@/machines/keys/batchInjectKey';
import { Jurisdiction } from '@/types/enums/Jurisdiction';

type PhoneCountry = {
  iso2: string;
};

const emit = defineEmits(['show-mobile-id-warning', 'done', 'cancel']);
const deepAdminStore = useDeepAdminStore();
const { deepSignAPI } = useApi();
const signStore = useSignStore();
const deepAdminInternalStore = useDeepAdminInternalStore();
const batchSignMachine = inject(batchProcessMachineKey, undefined);
const i18n = useI18n();
const emitter = useEmitter();
const route = useRoute();
const display = useDisplay();
const user = AuthService.user as {
  given_name?: string;
  family_name?: string;
  display_name?: string;
  sub?: string;
};

const form = reactive<formType>({
  firstname: user.given_name ?? '',
  lastname: user.family_name ?? '',
  displayName: user.display_name ?? '',
  phoneNumber: '',
  id: user.sub ?? ''
});

const phone = ref<ParsedPhoneNumber>();
const errors = ref<errorType>();
const updatePending = ref(false);

const bindProps = reactive({
  defaultCountry: 'CH',
  preferredCountries: ['CH', 'IT', 'FR', 'DE', 'AT', 'LI'],
  mode: 'international',
  disabledFetchingCountry: false,
  disabled: false,
  disabledFormatting: false,
  displayFormat: 'international',
  displayFormatDelay: 300,
  displayFormatOnBlur: false,
  required: true,
  enabledFlags: true,
  ignoredCountries: [],
  autocomplete: 'on',
  name: 'number',
  maxLen: constants.PHONE_FIELD.MAX_LENGTH,
  dropdownOptions: { disabledDialCode: true, tabindex: 0 },
  inputOptions: { showDialCode: false, tabindex: 0 },
  enabledCountryCode: false,
  validCharactersOnly: true
});

const isBatchRoute = computed(() => route.name === 'document-batch-sign');

const getPortalTitle = computed(() => {
  if (isBatchRoute.value) {
    return 'batch-identify-dialog-title';
  } else {
    return 'identify-dialog-title';
  }
});

const getPortalSubtitle = computed(() => {
  if (isBatchRoute.value) {
    return 'batch-identify-dialog-subtitle';
  } else {
    return 'identify-dialog-subtitle';
  }
});

const getPortalActions = computed(() => {
  if (isBatchRoute.value) {
    return 'batch-identify-dialog-actions';
  } else {
    return 'identify-dialog-actions';
  }
});

const displayNameErrors = computed(() => {
  return errors.value?.non_field_errors || [];
});

const isFormValid = computed(() => {
  return isMobilePhoneValid(phone.value);
});

function onPhoneUpdate(parsed: ParsedPhoneNumber) {
  phone.value = JSON.parse(JSON.stringify(parsed));
}

function canFormatPhone(parsed?: ParsedPhoneNumber): boolean {
  return Boolean(parsed && (parsed.valid || parsed.shortValid));
}

function isMobilePhoneValid(parsed?: ParsedPhoneNumber): boolean {
  return Boolean(parsed && canFormatPhone(parsed) && parsed.typeIsMobile);
}

function validatePhone(
  parsed: ParsedPhoneNumber,
  _country: PhoneCountry
): boolean {
  if (!parsed) return true;

  return canFormatPhone(parsed);
}

const invalidPhoneMessage = computed(() => {
  if (!phone.value) return;

  if (!canFormatPhone(phone.value)) return;

  if (phone.value.typeIsMobile) return;

  if (phone.value.regionCode === 'CH') {
    return i18n.t('errors.invalid_swiss_mobile_phone_number');
  }

  return i18n.t('errors.invalid_phone_number');
});

const phoneErrorMessages = computed(() => {
  return invalidPhoneMessage.value ? [invalidPhoneMessage.value] : [];
});

async function updateDeepAdminUser() {
  updatePending.value = true;
  try {
    await deepAdminStore.updateUser(form.id, {
      firstName: form.firstname,
      lastName: form.lastname,
      displayName: form.displayName,
      countryOfResidence: phone.value?.regionCode
        ? phone.value?.regionCode
        : '',
      primaryPhoneNumber: form.phoneNumber ? form.phoneNumber : ''
    });

    await refreshAvailableAuthorityServices();
  } catch (e) {
    errorMessageHandler(e);
  } finally {
    updatePending.value = false;
  }
}

async function refreshAvailableAuthorityServices() {
  const document = isBatchRoute.value
    ? batchSignMachine?.state.value.context.documents[0]
    : signStore.document;
  if (!document) {
    throw new Error('Missing document context for Mobile ID verification');
  }
  if (document.jurisdiction === Jurisdiction.GUEST_SES) {
    throw new Error('Mobile ID verification requires a supported jurisdiction');
  }

  const response =
    await deepSignAPI.value.usersMeAvailableAuthorityServices.get({
      signatureMode: document.signatureMode,
      jurisdiction: document.jurisdiction
    });
  const availableAuthorityServices =
    response.data as AvailableAuthorityServices;

  if (isBatchRoute.value) {
    deepAdminInternalStore.signeeIssues = availableAuthorityServices;
  } else if (signStore.document) {
    signStore.document.signContext.availableAuthorityServices =
      availableAuthorityServices;
  }

  mobileIdNextStep(Boolean(availableAuthorityServices.ras?.hasRequiredLevel));
}

function mobileIdNextStep(canUseMobileID: boolean) {
  if (canUseMobileID) {
    enableSmsCode();
  } else {
    emit('show-mobile-id-warning');
  }
}

function enableSmsCode() {
  emitter.$emit('enable-sms-code');
  emit('done');
}

function handleCancel() {
  emit('cancel');
}

function clearErrors() {
  errors.value = undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function errorMessageHandler(error: any) {
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (data.non_field_errors) {
      errors.value = { non_field_errors: data.non_field_errors };
    } else {
      errors.value = { non_field_errors: [i18n.t('errors.error_occurred')] };
    }
  } else {
    errors.value = { non_field_errors: [i18n.t('errors.error_occurred')] };
  }
}
</script>

<style scoped lang="scss">
.authority-service-logo {
  height: 20px;
}

.mobile-id-title {
  font-weight: 600;
  font-size: 22px;
  line-height: 28px;
  text-align: center;
  color: black;
  margin-bottom: 12px;
}

.mobile-id-subtitle {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  color: black;
  margin-bottom: 24px;
}

:deep(.v-text-field.v-text-field--enclosed .v-text-field__details) {
  padding-left: 0 !important;
}

:deep(.v-phone-input) {
  .v-field__input {
    padding-top: 0;
    padding-bottom: 0;
  }

  .v-input__details {
    align-items: flex-start;
    height: 30px;
    min-height: 30px;
    overflow: visible;
    padding-top: 6px;
  }
}
</style>
